import {createAction, createAsyncThunk} from '@reduxjs/toolkit';
import {normalize} from 'normalizr';
import {AppThunk, AppThunkApiConfig} from '../../createStore';
import parseISO from 'date-fns/parseISO';
import uniqBy from 'lodash/uniqBy';

import {withPayloadType} from '@lib/utils';
import {
  MeetingSlotSuggestionRequestData,
  MeetingPreferencesResult,
  NormalizedMeetingsResult,
  NormalizedMeetingSuggestionsResult,
  MeetingCreationRequestData,
  AvailabilityOfInviteesRequestData,
  NormalizedTimeZoneResult,
  MeetingUpdateRequestData,
} from './types';
import {
  AttendeeAvailability,
  Meeting,
  meetingEntity,
  MeetingInviteeSearchResult,
  meetingSuggestionEntity,
  timeZoneEntity,
  MeetingsUserPreferences,
} from '../entities';
import {
  cloudSuiteIntegrationErrors,
  mapSingleMeeting,
  mapMeetings,
  mapMeetingSuggestions,
  mapMeetingUserPreferences,
  mapWorkingHoursDtoToWorkingHours,
  getCalendarDomainPreference,
} from './helpers';
import {AsyncQueue, chunkifyRange} from '@lib/utils';
import {GenericError, handleError, ValidationError} from '../../infrastructure/errorHandler';
import {
  mapiqApi,
  MeetingRequestDto,
  MeetingSuggestionsRequestDto,
  AvailabilityOfInviteesRequestDto,
  trackEvent,
  NoShowReaction,
} from '@lib/infrastructure';
import {getInitialState} from './initialState';
import {getIsHybridMeetingsFeatureEnabled} from '../../infrastructure/features/selectors';
import {getHasUserGivenCalendarConsent, getIsMeetingsCalendarEnabled} from './selectors';
import {differenceInMinutes} from 'date-fns';
import {isFulfilled} from './utils';
import {isOccupancyEnabled} from '../../infrastructure/features/helpers';

export const loadMeetingPreferences = createAsyncThunk<MeetingPreferencesResult, void, AppThunkApiConfig>(
  'meetings/loadMeetingsPreferences',
  async (_arg, {extra, getState}) => {
    const state = getState();
    if (!getIsHybridMeetingsFeatureEnabled(state) && !isOccupancyEnabled(state.data.features.enabledFeatures)) {
      const {settings, userPreferences} = getInitialState();
      return {settings, userPreferences};
    }

    const {apiClient} = extra;
    const api = mapiqApi(apiClient);
    const [globalSettings, settings, userPreferences] = await Promise.all([
      api.getGlobalMeetingSettings(),
      api.getMeetingSettings(),
      api.getMeetingUserPreferences(),
    ]);

    return {
      settings: {
        ...globalSettings,
        ...settings,
      },
      userPreferences: {
        ...userPreferences,
        workingHours: mapWorkingHoursDtoToWorkingHours(userPreferences.workingHours),
      },
    };
  },
);

// For now these two are the same, but the refresh one does not update the loading state of the preferences intentionally,
// to avoid the calendar type to change unnecessarily and to load these unnecessarily (twice in the beginning)
export const refreshMeetingPreferences = createAsyncThunk<MeetingPreferencesResult, void, AppThunkApiConfig>(
  'meetings/refreshMeetingPreferences',
  async (_arg, {extra, getState}) => {
    if (!getIsHybridMeetingsFeatureEnabled(getState())) {
      const {settings, userPreferences} = getInitialState();
      return {settings, userPreferences};
    }

    const {apiClient} = extra;
    const api = mapiqApi(apiClient);
    const [globalSettings, settings, userPreferences] = await Promise.all([
      api.getGlobalMeetingSettings(),
      api.getMeetingSettings(),
      api.getMeetingUserPreferences(),
    ]);

    return {
      settings: {
        ...globalSettings,
        ...settings,
      },
      userPreferences: {
        ...userPreferences,
        workingHours: mapWorkingHoursDtoToWorkingHours(userPreferences.workingHours),
      },
    };
  },
);

export const setUserCalendarConsentAsInvalid = createAction(
  'meetings/userCalendarConsentInvalid',
  withPayloadType<boolean>(),
);

export const loadMeetings = createAsyncThunk<NormalizedMeetingsResult, {from: string; to: string}, AppThunkApiConfig>(
  'meetings/loadMeetings',
  async ({from, to}, {getState, extra, dispatch}) => {
    const state = getState();
    if (
      !getIsHybridMeetingsFeatureEnabled(state) ||
      !getIsMeetingsCalendarEnabled(state) ||
      !getHasUserGivenCalendarConsent(state)
    ) {
      dispatch(clearAllMeetings());
      return {
        meetingRooms: {},
        meetings: {},
      };
    }

    try {
      const {apiClient} = extra;
      const api = mapiqApi(apiClient);
      const calendarDomain = getCalendarDomainPreference(state);
      const meetingsResponse = await api.getMeetings(parseISO(from), parseISO(to), calendarDomain);
      const meetings = mapMeetings(meetingsResponse);
      const {entities} = normalize<any, NormalizedMeetingsResult>(meetings, [meetingEntity]);
      return entities;
    } catch (error) {
      const actualError = error as ValidationError;
      if (
        actualError &&
        actualError.data &&
        cloudSuiteIntegrationErrors.some((errorKey) => errorKey in actualError.data.errors)
      ) {
        dispatch(setUserCalendarConsentAsInvalid(true));
        // We want this error to be handled and displayed with the custom message
        throw new GenericError(extra.i18n.lookup('error:ConsentError'));
      }

      dispatch(setUserCalendarConsentAsInvalid(false));
      throw error;
    }
  },
);

export const createMeeting = createAsyncThunk<NormalizedMeetingsResult, MeetingCreationRequestData, AppThunkApiConfig>(
  'meetings/createMeeting',
  async (
    {startDateTime, endDateTime, attendees, rooms, origin, ...restOfProvidedData},
    {extra, dispatch, rejectWithValue, getState},
  ) => {
    const client = extra.apiClient;
    const api = mapiqApi(client);
    const calendarDomain = getCalendarDomainPreference(getState());
    try {
      const newMeetingDto: MeetingRequestDto = {
        calendarDomain,
        startDateTime,
        endDateTime,
        attendees: attendees,
        roomEmails: rooms,
        ...restOfProvidedData,
      };
      const createdMeeting = await api.postCreateMeeting(newMeetingDto);
      const mappedMeeting = mapSingleMeeting(createdMeeting);
      const {entities} = normalize<any, NormalizedMeetingsResult>(mappedMeeting, meetingEntity);

      const durationInMinutes = differenceInMinutes(parseISO(endDateTime), parseISO(startDateTime));
      trackEvent('MeetingCreated', {origin, durationInMinutes});

      return entities;
    } catch (error) {
      handleError(error, dispatch);
      return rejectWithValue('Create meeting failed');
    }
  },
  {
    condition: ({title, startDateTime, endDateTime}, {getState}) => {
      const {meetings} = getState().data;
      const {settings} = meetings;
      return (
        !!title &&
        !!startDateTime &&
        !!endDateTime &&
        getIsHybridMeetingsFeatureEnabled(getState()) &&
        settings.enabledCalendarIntegration
      );
    },
  },
);

export const loadMeetingTimeSlotSuggestions = createAsyncThunk<
  NormalizedMeetingSuggestionsResult,
  MeetingSlotSuggestionRequestData,
  AppThunkApiConfig
>(
  'meetings/loadMeetingTimeSlotSuggestions',
  async (data, {extra, dispatch, rejectWithValue, getState}) => {
    const client = extra.apiClient;
    const api = mapiqApi(client);
    const calendarDomain = getCalendarDomainPreference(getState());
    try {
      const request: MeetingSuggestionsRequestDto = {
        ...data,
        attendees: data.attendees,
        calendarDomain,
      };
      const apiResult = await api.postMeetingSuggestions(request);
      const suggestionsWithIds = mapMeetingSuggestions(apiResult);
      const {entities} = normalize<any, NormalizedMeetingSuggestionsResult>(suggestionsWithIds, [
        meetingSuggestionEntity,
      ]);
      return entities;
    } catch (error) {
      handleError(error, dispatch);
      return rejectWithValue('Meeting suggestion acquiring failed');
    }
  },
  {
    condition: (_arg, {getState}) => {
      const {settings} = getState().data.meetings;
      return getIsHybridMeetingsFeatureEnabled(getState()) && settings.enabledCalendarIntegration;
    },
  },
);

export const searchInvitees = createAsyncThunk<MeetingInviteeSearchResult[], string, AppThunkApiConfig>(
  'meetings/searchInvitees',
  async (searchTerm, {extra}) => {
    const trimmedSearch = searchTerm.trim();
    if (trimmedSearch.length < 2) {
      return [];
    }

    const client = extra.apiClient;
    const api = mapiqApi(client);

    const apiResult = await api.getInvitees(searchTerm);
    return apiResult as MeetingInviteeSearchResult[];
  },
  {
    condition: (_arg, {getState}) => {
      const {meetings} = getState().data;
      const {settings} = meetings;
      return getIsHybridMeetingsFeatureEnabled(getState()) && settings.enabledCalendarIntegration;
    },
  },
);

export const searchMultipleInvitees = createAsyncThunk<MeetingInviteeSearchResult[], string[], AppThunkApiConfig>(
  'meetings/searchMultipleInvitees',
  async (searchTerms, {extra, dispatch, rejectWithValue}) => {
    const trimmedSearchTerms = searchTerms
      .map((searchTerm) => searchTerm.trim())
      .filter((searchTerm) => searchTerm.length >= 2);

    if (!trimmedSearchTerms.length) return [];

    const client = extra.apiClient;
    const api = mapiqApi(client);

    try {
      const apiResult = await Promise.allSettled(trimmedSearchTerms.map((searchTerm) => api.getInvitees(searchTerm)));
      const filteredResult = apiResult.filter(isFulfilled).flatMap(({value}) => value);

      return uniqBy(filteredResult, 'id') as MeetingInviteeSearchResult[];
    } catch (error) {
      handleError(error, dispatch);
      return rejectWithValue([]);
    }
  },
  {
    condition: (_arg, {getState}) => {
      const {meetings} = getState().data;
      const {settings} = meetings;
      return getIsHybridMeetingsFeatureEnabled(getState()) && settings.enabledCalendarIntegration;
    },
  },
);

export const updateInviteeSearchTerm = createAction('meeting/updateInviteeSearchTerm', withPayloadType<string>());

export const setInviteeSearchStateAsThrottled = createAction(
  'meeting/InviteeSearchStatusThrottledSet',
  withPayloadType<void>(),
);

export const updateUserClosedConsentTilePreference = createAsyncThunk<boolean, boolean, AppThunkApiConfig>(
  'meetings/consentTileClosed',
  async (userClosedConsentTile, {extra}) => {
    const {apiClient} = extra;
    const api = mapiqApi(apiClient);
    await api.patchMeetingUserPreferences({userClosedConsentTile: userClosedConsentTile});
    return userClosedConsentTile;
  },
  {
    condition: (_arg, {getState}) => {
      const {meetings} = getState().data;
      const {settings} = meetings;
      return getIsHybridMeetingsFeatureEnabled(getState()) && settings.enabledCalendarIntegration;
    },
  },
);

export const connectCalendar = createAsyncThunk<boolean, boolean, AppThunkApiConfig>(
  'meetings/connectCalendar',
  async (_restoreConnection, {extra, dispatch}) => {
    try {
      const {apiClient, calendarService} = extra;
      const api = mapiqApi(apiClient);

      const {getSuccessRedirectUrl, getFailureRedirectUrl, getCancellationRedirectUrl} = calendarService;
      const consentUrl = await api.getCalendarConsentUrl(
        getSuccessRedirectUrl(),
        getFailureRedirectUrl(),
        getCancellationRedirectUrl(),
      );

      // Backend will return either null or empty string as indication that we already got the token for calendar access
      if (!consentUrl) {
        await api.patchMeetingUserPreferences({enableCalendar: true});
        dispatch(setUserCalendarConsentAsInvalid(false));
        return true;
      }

      const consentResult = await calendarService.requestConsent(consentUrl);
      if (!consentResult) return false;

      const {enableCalendar} = await api.patchMeetingUserPreferences({enableCalendar: true});
      dispatch(setUserCalendarConsentAsInvalid(!enableCalendar));
      return enableCalendar;
    } catch (error) {
      dispatch(setUserCalendarConsentAsInvalid(true));
      throw error;
    }
  },
  {
    condition: (_arg, {getState}) => {
      return getIsHybridMeetingsFeatureEnabled(getState());
    },
  },
);

export const disconnectCalendar = createAsyncThunk<void, void, AppThunkApiConfig>(
  'meetings/disconnectCalendar',
  async (_, {extra, dispatch}) => {
    const {apiClient} = extra;
    const api = mapiqApi(apiClient);

    await api.patchMeetingUserPreferences({enableCalendar: false});
    dispatch(setUserCalendarConsentAsInvalid(false));
    dispatch(clearAllMeetings());
  },
);

export const redirectForCalendarConsent = createAsyncThunk<void, void, AppThunkApiConfig>(
  'meetings/redirectForCalendarConsent',
  async (_, {extra, dispatch}) => {
    try {
      const {apiClient, calendarService} = extra;
      const api = mapiqApi(apiClient);
      const {getSuccessRedirectUrl, getFailureRedirectUrl, getCancellationRedirectUrl} = calendarService;
      const consentUrl = await api.getCalendarConsentUrl(
        getSuccessRedirectUrl(),
        getFailureRedirectUrl(),
        getCancellationRedirectUrl(),
      );

      // Backend will return either null or empty string as indication that we already got the token for calendar access
      if (!consentUrl) {
        await api.patchMeetingUserPreferences({enableCalendar: true});
        dispatch(setUserCalendarConsentAsInvalid(false));
        return;
      }

      calendarService.requestConsent(consentUrl);
    } catch (error) {
      dispatch(setUserCalendarConsentAsInvalid(true));
      throw error;
    }
  },
);

export const getConsentUrlForCalendar = createAsyncThunk<string | undefined, void, AppThunkApiConfig>(
  'meetings/getConsentUrlForCalendar',
  async (_, {extra, dispatch}) => {
    try {
      const {apiClient, calendarService} = extra;
      const api = mapiqApi(apiClient);
      const {getSuccessRedirectUrl, getFailureRedirectUrl, getCancellationRedirectUrl} = calendarService;
      const consentUrl = await api.getCalendarConsentUrl(
        getSuccessRedirectUrl(),
        getFailureRedirectUrl(),
        getCancellationRedirectUrl(),
      );

      // Backend will return either null or empty string as indication that we already got the token for calendar access
      if (!consentUrl) {
        await api.patchMeetingUserPreferences({enableCalendar: true});
        dispatch(setUserCalendarConsentAsInvalid(false));
        return;
      }

      return consentUrl;
    } catch (error) {
      dispatch(setUserCalendarConsentAsInvalid(true));
      throw error;
    }
  },
);

export const enableCalendarAfterRedirection = createAsyncThunk<boolean, void, AppThunkApiConfig>(
  'meetings/enableCalendarAfterRedirection',
  async (_, {extra, dispatch}) => {
    try {
      const {apiClient} = extra;
      const api = mapiqApi(apiClient);
      const {enableCalendar: result} = await api.patchMeetingUserPreferences({enableCalendar: true});
      dispatch(setUserCalendarConsentAsInvalid(!result));
      return result;
    } catch (error) {
      dispatch(setUserCalendarConsentAsInvalid(true));
      throw error;
    }
  },
);

export const deleteMeeting = createAsyncThunk<Meeting, Meeting, AppThunkApiConfig<Meeting>>(
  'meetings/deleteMeeting',
  async (meeting, {dispatch, rejectWithValue, extra}) => {
    const client = extra.apiClient;
    const api = mapiqApi(client);

    try {
      await api.deleteMeeting(meeting.id);
      return meeting;
    } catch (error) {
      return rejectWithValue(meeting);
    }
  },
);

export const selectMeeting = createAction('meetings/meetingSelected', withPayloadType<string>());

export const unselectMeeting = createAction('meetings/meetingUnselected', withPayloadType<void>());

export const selectMeetingAttendeeGroupItem = createAction(
  'meetings/itemMeetingDetailsSelected',
  withPayloadType<string>(),
);

export const setLastRequestedDate = createAction('meetings/lastRequestedDateSet', withPayloadType<string>());

export const clearLastRequestedDate = createAction('meetings/lastRequestedDateClear', withPayloadType<void>());

export const clearAllMeetings = createAction('meetings/allMeetingsClear', withPayloadType<void>());

export const loadAllMeetings =
  ({from, to, pageSize = 7}: {from: string; to: string; pageSize?: number}): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const {settings, userPreferences, isUserCalendarConsentInvalid} = state.data.meetings;
    const canLoadMeetings =
      getIsHybridMeetingsFeatureEnabled(state) &&
      settings.enabledCalendarIntegration &&
      userPreferences.enableCalendar &&
      !isUserCalendarConsentInvalid &&
      pageSize;

    if (!canLoadMeetings) {
      return;
    }

    dispatch(clearAllMeetings());

    const fromDate = parseISO(from);
    const toDate = parseISO(to);
    const chunks = chunkifyRange(fromDate, toDate, pageSize);

    for (const [startDate, endDate] of chunks) {
      await AsyncQueue.enqueue(() =>
        dispatch(
          loadMeetings({
            from: startDate.toISOString(),
            to: endDate.toISOString(),
          }),
        ),
      );
    }
  };
export const loadTimeZones = createAsyncThunk<NormalizedTimeZoneResult, void, AppThunkApiConfig>(
  'meeting/loadTimeZones',
  async (_, {extra}) => {
    const {apiClient} = extra;
    const api = mapiqApi(apiClient);

    const result = await api.getAvailableTimeZones();

    const {entities} = normalize<any, NormalizedTimeZoneResult>(result, [timeZoneEntity]);

    return entities;
  },
);
export const loadAvailabilityOfInviteesSuggestions = createAsyncThunk<
  AttendeeAvailability[],
  AvailabilityOfInviteesRequestData,
  AppThunkApiConfig
>('meetings/loadAvailabilityOfInviteesSuggestions', async ({attendeeEmails, endDateTime, startDateTime}, {extra}) => {
  const client = extra.apiClient;
  const api = mapiqApi(client);
  const availabilityOfInviteesSuggestionsRequestData: AvailabilityOfInviteesRequestDto = {
    emails: attendeeEmails,
    endDateTimeUTC: endDateTime.toISOString(),
    startDateTimeUTC: startDateTime.toISOString(),
  };
  const apiResult = await api.postAvailabilityOfInviteesSuggestions(availabilityOfInviteesSuggestionsRequestData);
  return apiResult as AttendeeAvailability[];
});

export const patchMeetingUserPreferences = createAsyncThunk<
  MeetingsUserPreferences,
  Partial<Required<MeetingsUserPreferences>>,
  AppThunkApiConfig
>('meetings/patchMeetingUserPreferences', async (data, {extra}) => {
  const api = mapiqApi(extra.apiClient);

  const mappedData = mapMeetingUserPreferences(data);
  const result = await api.patchMeetingUserPreferences(mappedData);

  return {
    ...result,
    workingHours: mapWorkingHoursDtoToWorkingHours(result.workingHours),
  };
});

export const updateMeeting = createAsyncThunk<NormalizedMeetingsResult, MeetingUpdateRequestData, AppThunkApiConfig>(
  'meetings/updateMeeting',
  async ({origin, meetingId, systemTimeZone, rooms, ...restOfProvidedData}, {extra, getState, dispatch}) => {
    const api = mapiqApi(extra.apiClient);

    // Remapping and adding the non-provided data
    const state = getState();
    const currentData = state.data.meetings.items.entities[meetingId];
    const calendarDomain = getCalendarDomainPreference(state);

    const newData: MeetingRequestDto = {
      calendarDomain,
      attendees: currentData?.attendees.map((attendee) => attendee.email) ?? [],
      startDateTime: currentData?.startDateTime ?? '',
      endDateTime: currentData?.endDateTime ?? '',
      organizerTimeZone: currentData?.organizerTimeZone ?? systemTimeZone,
      isAllDay: currentData?.isAllDay ?? false,
      isOnlineMeeting: !!currentData?.onlineMeetingUrl,
      message: currentData?.message ?? '',
      title: currentData?.title ?? '',
      ...restOfProvidedData,
      roomEmails: rooms ?? currentData?.meetingRooms ?? [],
    };

    const apiResult = await api.putMeeting(meetingId, newData);
    const mappedMeeting = mapSingleMeeting(apiResult);
    const {entities} = normalize<any, NormalizedMeetingsResult>(mappedMeeting, meetingEntity);

    trackEvent('MeetingEdited', {origin});

    return entities;
  },
);

export const respondToNoShowDetected = createAsyncThunk<
  void,
  {noShowId: string; response: NoShowReaction},
  AppThunkApiConfig
>('meetings/respondToNoShowDetected', async ({noShowId, response}, {extra}) => {
  const api = mapiqApi(extra.apiClient);

  await api.putNoShowResponse(noShowId, response);
});
